import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Directive, ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Inject,
  Input, OnDestroy, OnInit,
  Output, ViewChild, ViewContainerRef
} from '@angular/core';
import {
  SohoBusyIndicatorDirective,
  SohoMessageService, SohoModalDialogRef,
  SohoModalDialogService,
  SohoToastService
} from 'ids-enterprise-ng';
import {
  AsOfDateDialogStatus,
  AsOfDateResult, LMActionFormDialogResult, LMActionFormDialogResultCode,
  LMActionFormDialogService,
  LMApplicationMenuAsOfDateDialogService, LMApplicationMenuProxyMenuComponent,
  LMApplicationMenuSearchService,
  LMApplicationMenuService,
  LMApplicationMenuSettingsDialogComponent,
  LMApplicationMenuUserContextDialogService, LMApplicationMenuUserContextDialogStatus,
  LMApplicationMenuUserContextResult,
  LMFormRequestService,
  LMLoginService, LMReportPersonalizationDialogResult, LMReportPersonalizationDialogResultCode,
  LMReportPersonalizationDialogService, LoginDialogStatus, LoginResult
} from '@app/components';
import {
  LMBusyIndicatorService,
  LMContextService,
  LMErrorUtilService,
  LMFormatUtilService,
  LMLoggerService,
  LMRoamingUIProfileService,
  LMSpecService,
  LMUrlUtilService,
  RoamingUIProfileResponse,
  RoamingUIProfileResult
} from '@app/services';
import {
  AppConstants,
  LMContextUtilService,
  LMDialogButtonKey,
  LMGlobalUtilService,
  LMUniqueIdService
} from '@app/utils';
import { LMGlobalPersonalization, LMGlobalPersonalizationService } from '@app/states';
import { Router } from '@angular/router';
import { EMPTY, forkJoin, interval, Observable, of, Subject } from 'rxjs';
import { SubscriptionLike as ISubscription } from 'rxjs/internal/types';
import { catchError, debounceTime, first, switchMap, takeUntil } from 'rxjs/operators';
import * as urltemplate from 'url-template';
import * as _ from 'lodash';
import { LMThemeService } from '@banner/lm-theme.service';

interface ApplicationMenuButton {
  icon: string;
  label: string;
  id: string;
  url: string;
  automationId: string;
}

@Directive()
export abstract class LMApplicationMenuBaseDirective implements OnInit, AfterViewInit, OnDestroy, AfterViewChecked {
  private notificationRefreshInterval: number;
  private unreadNotificationCount: number;
  private _displayRedDot = false;
  private m_asOfDate: string;
  private m_showDeletedRecords: boolean;
  private stopInterval$ = new Subject<boolean>();
  private pause$ = new Subject<boolean>();
  public searchFieldText$ = new Subject<string>();
  private destroyer$ = new Subject<void>();
  private dialog: SohoModalDialogRef<any>;
  private subscriptionArray: Array<ISubscription> = []; // todo: type me!

  public mingleMode: boolean;
  public applicationMenuSpecs: Array<LMWebAppSpec>;
  public webAppMenuSpecs: Array<any>;  // todo: type me!
  public aboutLink: string;
  public appHelpLink: string;
  public notificationsUrl: string;
  public inBasketUrl: string;
  public jobsUrl: string;
  public printFilesUrl: string;
  public actionsUrl: string;
  public reportsUrl: string;
  public personalizationsUrl: string;
  public mySavedSearchesUrl: string;
  public actorName: string;
  public actorInitials: string;
  public actorInitialsColorClass: string;
  public anonymousUser: boolean;
  public proxyMode: boolean;
  public notificationEnabled: boolean;
  public inbasketEnabled: boolean;
  public personalizationEnabled: boolean;
  public enableSavedSearch: boolean;
  public proxyEnabled: boolean;
  public selectedMenuSpec: Array<any>; // todo: type me!
  public menuSpecs: Array<any>; // todo: type me!
  public searchMenuSpecs: Array<any>;
  public webAppTitle: string;
  public webAppName: string;
  public selectedWebAppName: string;
  public webAppSuiteLink: string;
  public appSwitcher: LMApplicationMenuAppMenuMode;
  public webAppMenuNavigationUrl: string;
  public signOutLink: string;
  public signInLink: string;
  public registerLink: string;
  public changePasswordLink: string;
  public proxyManagementLink: string;
  public createReportUrl: string;
  public actorGroupSelectUrl: string;
  public roleSelectUrl: string;
  public roleGroupSelectUrl: string;
  public reportFieldSearchUrl: string;
  public enableUserContextMenu: boolean;
  public hasRegisterLink: boolean;
  public hasChangePasswordLink: boolean;
  public allowSetAsOfDate: boolean;
  public allowClearAsOfDate: boolean;
  public searchMode: boolean;
  public updateRequired: boolean;
  public roleSwitcher: boolean;
  public noCasualApps: number;
  public clientLogin: boolean;
  public actorUrl: string;
  public proxyUserUrls: any;
  public proxyWebApps: Array<string> = [];  // todo: type me!
  public proxyResetUrls: any;
  public menuSearchField: LMToolbarSearchField;
  public model = { searchFieldValue: '' };
  public menuSearchResultsByWebApp: LMApplicationMenuSearchResults;
  public reportSearchResult: Array<LMApplicationMenuSearchResult>;
  public dataSearchResult: Array<LMApplicationMenuSearchResult>;
  public allowAnonymousAccess: boolean;

  // Button menu
  public applicationMenuButtons: ApplicationMenuButton[] = [];

  // Single button
  public notificationsButton: ApplicationMenuButton;
  public createReportButton: ApplicationMenuButton;
  public printFilesButton: ApplicationMenuButton;
  public inbasketButton: ApplicationMenuButton;

  public showSearch: boolean;
  public isClassicTheme: boolean;
  public rolesPaneIsOpen = false;
  public configFeaturesEnabled: boolean;
  public showProxy: boolean;

  public get displayRedDot(): boolean {
    return this._displayRedDot;
  }

  public set displayRedDot(display: boolean) {
    this._displayRedDot = display;
  }

  public get isPasswordReset(): boolean {
    return this.lmContextService.getContext().completePasswordReset;
  }

  @Input() visibility: string;

  @Input() set asOfDate(date: string) {
    this.allowClearAsOfDate = date !== undefined && date !== '';

    this.m_asOfDate = date;

    // only load menu when initial load has been done
    if (this.applicationMenuSpecs) {
      this.menuSpecs = [];
      this._loadMenu(this.lmUrlUtilService.getBannerSpecUrl());
    }
  }

  get asOfDate() {
    return this.m_asOfDate;
  }

  @Input() set showDeletedRecords(show: boolean) {
    this.m_showDeletedRecords = show;

    // only load menu when initial load has been done
    if (this.applicationMenuSpecs) {
      this.menuSpecs = [];
      this._loadMenu(this.lmUrlUtilService.getBannerSpecUrl());
    }
  }

  get showDeletedRecords() {
    return this.m_showDeletedRecords;
  }

  @Input() applyColorToMenu: boolean;

  @HostBinding('style.visibility') get hostVisibility() {
    return this.visibility;
  };

  @HostBinding('style.overflow') get hostOverflow() {
    return this.rolesPaneIsOpen ? 'hidden' : null;
  }

  @Output() menuLoaded: EventEmitter<any> = new EventEmitter<any>();
  @Output() menuStateRestored: EventEmitter<any> = new EventEmitter<any>();
  @Output() webAppMenuLoaded: EventEmitter<any> = new EventEmitter<any>();
  @Output() menuSearched: EventEmitter<any> = new EventEmitter<any>();
  @Output() asOfDateChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() asOfDateCleared: EventEmitter<string> = new EventEmitter<string>();
  @Output() roleSelected = new EventEmitter<any>();
  @Output() themeChanged: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('UserSettingsDialog', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;
  @ViewChild('AccessDeniedDialog', { read: ViewContainerRef, static: true }) accessDeniedplaceholder: ViewContainerRef;
  @ViewChild('LoginDialog', { read: ViewContainerRef, static: true }) loginPlaceholder: ViewContainerRef;
  @ViewChild('ChangePasswordDialog', { read: ViewContainerRef, static: true }) changePasswordPlaceholder: ViewContainerRef;
  @ViewChild('DrillbackActionFormDialog', { read: ViewContainerRef, static: true }) drillbackPlaceholder: ViewContainerRef;
  @ViewChild('ReportPersonalizationDialog', { read: ViewContainerRef, static: true }) reportPlaceholder: ViewContainerRef;
  @ViewChild('SetAsOfDateDialog', { read: ViewContainerRef, static: true }) setAsOfDatePlaceholder: ViewContainerRef;
  @ViewChild('UserContextDialog', { read: ViewContainerRef, static: true }) userContextPlaceholder: ViewContainerRef;
  @ViewChild('webAppMenuHeader') private _webAppMenu: ElementRef;
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;

  constructor(
    protected sohoModalDialogService: SohoModalDialogService,
    protected sohoToastService: SohoToastService,
    protected lmAsOfDateService: LMApplicationMenuAsOfDateDialogService,
    protected lmContextService: LMContextService,
    protected lmApplicationMenuService: LMApplicationMenuService,
    protected lmFormRequestService: LMFormRequestService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmSpecService: LMSpecService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected lmLoggerService: LMLoggerService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmGlobalUtilService: LMGlobalUtilService,
    protected lmLoginService: LMLoginService,
    protected lmContextUtilService: LMContextUtilService,
    protected lmReportPersonalizationDialogService: LMReportPersonalizationDialogService,
    protected lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected sohoMessageService: SohoMessageService,
    protected lmApplicationMenuSearchService: LMApplicationMenuSearchService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmUserContextService: LMApplicationMenuUserContextDialogService,
    @Inject(forwardRef(() => LMActionFormDialogService)) protected lmActionFormDialogService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected router: Router,
    protected lmRoamingUIProfileService: LMRoamingUIProfileService,
    protected lmThemeService: LMThemeService
  ) {
    this.searchFieldText$.pipe(
      takeUntil(this.destroyer$),
      debounceTime(1000))
      .subscribe(value => {
        const length = value ? value.length : 0;
        if (length > 2) {
          this.applicationMenuSearch();
        }
      });
  }

  ngOnInit() {
    this.lmLoggerService.trace('@ApplicationMenuBaseDirective -> ngOnInit');
    this.searchMode = false;
    this.updateRequired = false;
    this.menuSpecs = [];
    this.roleSwitcher = false;
    this.webAppSwitcherButtonId = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1 ? 'trigger-btn' : 'webapp-menu';
    this.getApplicationMenuPersonalization();

    this.allowAnonymousAccess = this.lmContextService.allowAnonymousAccess();
    this.configFeaturesEnabled = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_ConfigurableFeatures);
    this.showSearch = this.evaluateShowSearch();

    this.lmThemeService.getThemeChangeSubject().subscribe((theme) => {
      if (theme.includes('theme-classic') && !this.isClassicTheme) {
        this.isClassicTheme = true;
        this.changeDetectorRef.markForCheck();
      } else {
        this.isClassicTheme = false;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngAfterViewInit() {
    this.mingleMode = this.lmContextService.inMingle();
    this._loadMenu(this.lmUrlUtilService.getBannerSpecUrl());
  }

  ngOnDestroy() {
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
    this.stopInterval$.next(true);
    this.stopInterval$.complete();
    this.pause$.complete();
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  public ngAfterViewChecked(): void {
    if (this.updateRequired) {
      this.onWebAppMenuLoaded();
      this.updateRequired = false;
    }
  }

  /**
   * Set href url when a right click is executed
   * @param event
   * @param menuItem
   */
  public onRoleRightClick(event: MouseEvent, menuItem: LMMenuItemSpec) {
    if (menuItem && menuItem.webAppUrl) {
      (event.currentTarget as HTMLElement).setAttribute('href', menuItem.webAppUrl);
    }
  }

  public selectRole($event?: any) {
    this.menuSpecs = [];
    this.roleSwitcher = true;
    this.changeDetectorRef.detectChanges();
    this.roleSelected.emit();

    this.showBusyIndicator();

    const button: any = $event.currentTarget;
    let url = button.getAttribute('navigation-menu-url');

    this.webAppTitle = button.getAttribute('title');
    this.webAppName = button.getAttribute('web-app-name');

    // For webapp without a navigation menu
    if (!url) {
      this.selectedMenuSpec = [];
      this.webAppMenuSpecs = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
      const currWebAppSpec = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);

      this.selectedMenuSpec.push(
        {
          resourceId:   currWebAppSpec[ 0 ].webAppName,
          name:         'Home',
          link:         currWebAppSpec[ 0 ].webAppUrl,
          id:           'Home',
          label:        Soho.Locale.translate('Home'),
          resourceType: 'page'
        });

      this.buildMenuSpecs(this.menuSpecs, this.webAppName, this.webAppTitle, this.selectedMenuSpec);
      this.markForRefresh();
      this.updateRequired = true;

      this.lmContextService.updateWebApp(currWebAppSpec[ 0 ]);
      this.lmGlobalUtilService.navigateByUrl(currWebAppSpec[ 0 ].webAppUrl);

      this.hideBusyIndicator();
    } else {

      const loadAllLevels = this.lmApplicationMenuService.loadAllLevels();

      if (loadAllLevels) {
        url = `${url}&allLevels=true`;
      }

      this.subscriptionArray.push(
        this.lmSpecService.getApplicationMenuSpec(url).subscribe(
          response => {
            if (loadAllLevels) {
              this.saveAllLevelsResponse(response);
            }

            this.selectedMenuSpec = response.menuSpecs;

            this.buildMenuSpecs(this.menuSpecs, response.webApp, response.webAppTitle, this.selectedMenuSpec);

            this.signOutLink = response.signOutLink;

            this.resetMenuPersonalization();

            this.lmApplicationMenuService.setApplicationMenuSpec(this.selectedMenuSpec);

            this.lmApplicationMenuService.updateApplicationMenuSpec(response.menuSpecs, url);

            this.markForRefresh();
            this.updateRequired = true;

            this.webAppMenuSpecs = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);

            const currWebAppSpec = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);
            this.lmContextService.updateWebApp(currWebAppSpec[ 0 ]);

            this.lmGlobalUtilService.navigateByUrl(currWebAppSpec[ 0 ].webAppUrl);

            this.hideBusyIndicator();
          },
          error => {
            this._handleError(error);
          })
      );
    }
  }

  public get webAppMenuHeader(): ElementRef {
    const webAppMenuName = this.selectedWebAppName ? this.selectedWebAppName : this.webAppName;
    // @ts-ignore

    if (this._webAppMenu && this._webAppMenu.nativeElement.getAttribute('id') === webAppMenuName) {
      return this._webAppMenu;
    }
    return null;
  }

  public onMenuLoaded(event) {
    this.menuLoaded.emit(event);
  }

  public onWebAppMenuLoaded() {
    this.webAppMenuLoaded.emit(this.roleSwitcher);
  }

  public onMenuSearched(event: LMApplicationMenuSearchEvent) {
    this.menuSearched.emit(event);
  }

  public openUserSettingsDialog() {
    const dialogId = 'UserSettings';
    const buttons = [
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
        click: (e, modal) => {
          modal.isCancelled = true;
          modal.close(true);
        }
      },
      {
        id:    this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.SAVE ]),
        text:  Soho.Locale.translate(LMDialogButtonKey.SAVE),
        click: (e, modal) => {
          modal.close(true);
        },
        isDefault: true
      }
    ];

    const settingsModalAttributes: SohoAttributesArray = [
      { name: 'data-automation-id', value: 'lm-settings-modal' }
    ];

    this.dialog = this.sohoModalDialogService
      .modal(
        LMApplicationMenuSettingsDialogComponent,
        this.placeholder,
        { fullsize: 'responsive', attributes: settingsModalAttributes })
      .buttons(buttons)
      .title(Soho.Locale.translate(dialogId))
      .open();
  }

  public openSetAsOfDateDialog(): void {
    const date = this.lmContextService.getContext().asOfDate ? this.lmFormatUtilService.yyyymmddToDisplay(this.asOfDate) : null;
    this.lmAsOfDateService.promptAsOfDateDialog(this.setAsOfDatePlaceholder, date)
      .pipe(first())
      .subscribe((result: AsOfDateResult) => {
        if (result.status !== AsOfDateDialogStatus.RESPONSE_OK) {
          return;
        }
        this.lmLoggerService.log(result.date);
        const dbDate = this.lmFormatUtilService.yyyymmddToData(result.date);
        this.asOfDateChanged.emit(dbDate);
      });
  }

  public clearAsOfDate() {
    this.asOfDateCleared.emit();
  }

  public openSetUserContextDialog() {
    const userContextUrl = this.lmUrlUtilService.getUserContextUrl();

    this.lmSpecService.getApplicationMenuSpec(userContextUrl).subscribe(
      (response: any) => {
        const userContextDialog = this.lmUserContextService.promptUserContextDialog(this.userContextPlaceholder, response.userContextFields)
          .subscribe(
            (result: LMApplicationMenuUserContextResult) => {
              if (result.status === LMApplicationMenuUserContextDialogStatus.RESPONSE_OK) {
                if (result.userContextProperties && result.saveChanges) {

                  const jsonContextPropertiesArray = [];
                  Object.keys(result.userContextProperties).forEach(key => {
                    const userContextModel: LMApplicationMenuUserContextModelField = result.userContextProperties[ key ];
                    if (userContextModel.dirty) {
                      const jsonContextProperty = {
                        id:    key,
                        value: userContextModel.value
                      };
                      jsonContextPropertiesArray.push(jsonContextProperty);
                    }
                  });

                  this.lmApplicationMenuService.updateActorContext(userContextUrl, jsonContextPropertiesArray).subscribe((ucResponse: any) => {
                    if (ucResponse.status === 'COMPLETED') {
                      window.location.href = this.lmContextService.getCurrentWebAppSpec().webAppUrl;
                    }
                  });
                }
              }
            }
          );
        this.markForRefresh();
        // this.updateRequired = true;
        this.subscriptionArray.push(userContextDialog);
      },
      error => {
        this._handleError(error);
      });
    return false;
  }

  public noActorUrl() { // tslint:disable-line (template)
    return (this.actorUrl === '#' || this.actorUrl == null);
  }

  public openLink(link: string) {
    const compiledTemplate = urltemplate.parse(link);
    link = compiledTemplate.expand({
      urlPdlOrDa: this.lmContextService.getUrlPdlOrDa(),
      webApp:     this.lmContextService.getCurrentWebAppSpec().webAppName
    });
    this.lmContextService.setClearNextPageState(true);
    this.lmGlobalUtilService.navigateByUrl(link);
  }

  public openNotificationsLink(link: string) {
    this.displayRedDot = false;
    // Convert timestamp from simplified ISO format (YYYY-MM-DDTHH:mm:ss.sssZ) to Landmark format (yyyyMMddHHmmssSS)
    const curTimeStamp = new Date().toISOString().slice(-24).replace(/\D/g, '').slice(0, 16);
    this.lmRoamingUIProfileService.setLastViewedNotification(curTimeStamp)
      .subscribe(
        (results: RoamingUIProfileResponse) => {
          if (results.status === RoamingUIProfileResult.COMPLETED) {
            this.lmLoggerService.log('RoamingUIProfile Updated');
          }
        });
    this.openLink(link);
  }

  public openHelpLink() {
    this.lmUrlUtilService.openExternalUrl(this.appHelpLink);
  }

  public openSecurityLink(link: string) {
    this.resetLastOpenedMenu();
    window.location.href = link;
  }

  private resetLastOpenedMenu() {
    const applicationMenuPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();

    if (!_.isEmpty(applicationMenuPersonalization) && applicationMenuPersonalization.lastOpenedMenu) {
      applicationMenuPersonalization.lastOpenedMenu = '';
    }
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(applicationMenuPersonalization);
  }

  public _loadMenu(url: string, syncWebApp?: boolean): void {
    this.lmLoggerService.trace('@ApplicationMenuBaseDirective -> loadApplicationMenuSpec');
    this.showBusyIndicator();

    if (syncWebApp) {
      this.webAppMenuSpecs = [];
      this.markForRefresh();
      this.updateRequired = true;
    }

    this.subscriptionArray.push(this.lmSpecService.getApplicationMenuSpec(url).subscribe(
      (applicationMenuSpec: LMApplicationMenuSpec) => {
        this.applicationMenuSpecs = applicationMenuSpec.webApps;
        this.appSwitcher = 'casual'; // applicationMenuSpec.appSwitcher || 'accordion';
        this.noCasualApps = applicationMenuSpec.noCasualApps;
        this.aboutLink = applicationMenuSpec.aboutLink;
        this.appHelpLink = applicationMenuSpec.appHelpLink;
        this.actionsUrl = applicationMenuSpec.myActionsUrl;
        this.inBasketUrl = applicationMenuSpec.myInBasketUrl;
        this.jobsUrl = applicationMenuSpec.myJobsUrl;
        this.notificationsUrl = applicationMenuSpec.myNotificationsUrl;
        this.printFilesUrl = applicationMenuSpec.myPrintFilesUrl;
        this.reportsUrl = applicationMenuSpec.myReportsUrl;
        this.personalizationsUrl = applicationMenuSpec.myPersonalizationsUrl;
        this.mySavedSearchesUrl = applicationMenuSpec.mySavedSearchesUrl;
        this.createReportUrl = this.evaluateCreateReportUrl(applicationMenuSpec);
        this.actorGroupSelectUrl = applicationMenuSpec.actorGroupSelectUrl;
        this.roleSelectUrl = applicationMenuSpec.roleSelectUrl;
        this.roleGroupSelectUrl = applicationMenuSpec.roleGroupSelectUrl;
        this.reportFieldSearchUrl = applicationMenuSpec.reportFieldSearchUrl;
        this.signOutLink = applicationMenuSpec.signOutLink;
        this.registerLink = applicationMenuSpec.registerLink;
        this.signInLink = applicationMenuSpec.signInLink;
        this.changePasswordLink = applicationMenuSpec.changePasswordLink;
        this.actorName = applicationMenuSpec.actorInfo.displayName[ 0 ];
        this.actorInitials = this.getActorInitials();
        this.actorInitialsColorClass = this.lmThemeService.getUniqueBackgroundColorClass(this.actorInitials);
        this.actorUrl = applicationMenuSpec.actorInfo.actorUrl[ 0 ];

        // Proxy
        this.proxyUserUrls = applicationMenuSpec.proxyUserUrls;
        if (this.proxyUserUrls) {
          this.proxyWebApps = Object.keys(this.proxyUserUrls);
        }
        this.proxyResetUrls = applicationMenuSpec.proxyResetUrls;
        this.proxyMode = applicationMenuSpec.proxyMode || false;
        this.proxyEnabled = applicationMenuSpec.proxyEnabled || false;
        this.proxyManagementLink = applicationMenuSpec.proxyManagementLink;
        this.showProxy = this.evaluateProxy(applicationMenuSpec);

        this.anonymousUser = applicationMenuSpec.anonymousUser || false;
        this.clientLogin = applicationMenuSpec.clientLogin || false;
        this.webAppMenuNavigationUrl = applicationMenuSpec.webAppMenuNavigationUrl;
        if (this.lmGlobalPersonalizationService.getGlobalPersonalization().preferredWebApp !== undefined) {
          const preferredAppName = this.lmGlobalPersonalizationService.getGlobalPersonalization().preferredWebApp;
          const preferredAppSpec = this.applicationMenuSpecs.find((preferredApp: LMWebAppSpec) => preferredApp.webAppName === preferredAppName);
          if ((this.lmContextService.getContext().webApp === preferredAppName) && preferredAppSpec !== undefined) {
            this.webAppMenuNavigationUrl = preferredAppSpec.navigationMenuUrl;
          }
        }
        this.webAppTitle = this.lmContextService.getContext().webAppTitle;
        this.webAppName = this.lmContextService.getContext().webApp;
        this.selectedWebAppName = this.webAppName;
        this.webAppSuiteLink = applicationMenuSpec.webAppSuiteLink;

        this.hasRegisterLink = (this.registerLink === '' || this.registerLink == null) ? false : true;
        this.hasChangePasswordLink = (this.changePasswordLink === '' || this.changePasswordLink == null) ? false : true;

        this.allowSetAsOfDate = this.evaluateAsOfDate(applicationMenuSpec);
        this.allowClearAsOfDate = this.asOfDate !== undefined && this.asOfDate !== '';

        this.enableUserContextMenu = applicationMenuSpec.enableUserContextMenu;

        // TODO: Akila, is this right? Seems to always be true. And ignores the config param setting
        this.inbasketEnabled = applicationMenuSpec.inbasketEnabled || true;
        this.notificationEnabled = applicationMenuSpec.notificationEnabled || true;
        this.personalizationEnabled = this.evaluatePersonalization(applicationMenuSpec);
        this.enableSavedSearch = this.lmContextService.showUserListView();

        if (this.isPasswordReset) {
          this.hideBusyIndicator();
          this.markForRefresh();
          this.updateRequired = true;
          this.lmLoggerService.trace('@ApplicationMenuBaseDirective -> loadApplicationMenuSpec -> complete');
          return;
        }

        this.updateMenuButtons();

        if (this.notificationEnabled) {
          this.notificationRefreshInterval = (applicationMenuSpec.refreshIntervalMinutes) ? parseInt(applicationMenuSpec.refreshIntervalMinutes, 10) : 5;
          this.unreadNotificationCount = (applicationMenuSpec.unreadCount) ? parseInt(applicationMenuSpec.unreadCount, 10) : 0;
          this.displayRedDot = this.unreadNotificationCount > 0;

          // Poll to check unread notification count
          const interval$ = interval(this.notificationRefreshInterval * 60 * 1000).pipe(
            takeUntil(this.stopInterval$)
          );
          // Pause during async call to get the notification count.
          const pausable$ = this.pause$.pipe(
            switchMap(paused => paused ? EMPTY : interval$));

          this.subscriptionArray.push(pausable$.subscribe(() => {
            this.getUnreadNotificationCount();
          }));

          this.pause$.next(false);
        }

        if (!this.webAppMenuNavigationUrl) {
          if (this.useAppSwitcher()) {
            this.selectedMenuSpec = [];
            this.webAppMenuSpecs = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
            if (this.webAppMenuSpecs.length === 0 || this.webAppMenuSpecs.length > this.noCasualApps - 1) {
              this.roleSwitcher = true;
            }

            const currWebAppSpec = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.webAppName);
            this.selectedMenuSpec.push(
              {
                resourceId:   this.webAppName,
                name:         'Home',
                link:         this.webAppName === 'web' ? this.webAppSuiteLink : currWebAppSpec[ 0 ].webAppUrl,
                id:           'Home',
                label:        Soho.Locale.translate('Home'),
                resourceType: 'page'
              });

            this.buildMenuSpecs(this.menuSpecs, this.webAppName, this.webAppTitle, this.selectedMenuSpec);

            this.markForRefresh();
            this.updateRequired = true;
          } else {
            this.lmApplicationMenuService.updateApplicationMenuSpec(applicationMenuSpec.webApps);
          }
        } else {
          if (this.useAppSwitcher()) {
            this.loadWebAppMenuSpec();
          } else {
            this.lmApplicationMenuService.updateApplicationMenuSpec(applicationMenuSpec.webApps);
          }
        }

        this.hideBusyIndicator();
        this.lmLoggerService.trace('@ApplicationMenuBaseDirective -> loadApplicationMenuSpec -> complete');
      },
      error => {
        // acces denied error is handled in app.component.ts
        if (error.status !== 403) {
          this._handleError(error);
        }
      }));
  }

  private saveAllLevelsResponse(response) {
    const self = this;
    const expandedMenus = this.lmApplicationMenuService.getExpandedMenus();

    if (expandedMenus && expandedMenus.length > 0) {
      // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
      response.menuSpecs.map(function callback(menuItemSpec: LMMenuItemSpec) {
        if (expandedMenus.includes(menuItemSpec.menuIndex)) {
          menuItemSpec.expanded = true;
        }

        if (menuItemSpec.menuIndex === self.lmApplicationMenuService.getLastMenuItemClicked()) {
          menuItemSpec.selected = true;
        }

        if (menuItemSpec.menu && menuItemSpec.menu.length > 0) {
          menuItemSpec.menu.map(callback);
        }
      });
    }

    this.lmApplicationMenuService.saveAllLevelsResponse(response);
  }

  private loadWebAppMenuSpec() {
    const self = this;
    const loadAllLevels = this.lmApplicationMenuService.loadAllLevels();
    let webAppMenuNavigationUrl = this.webAppMenuNavigationUrl;

    if (loadAllLevels) {
      webAppMenuNavigationUrl = `${this.webAppMenuNavigationUrl}&allLevels=true`;
    }

    this.subscriptionArray.push(this.lmSpecService.getApplicationMenuSpec(webAppMenuNavigationUrl)
      .subscribe(async response => {
        if (loadAllLevels) {
          this.saveAllLevelsResponse(response);
        } else {
          /**
           *  Check state to see if there were previously expanded menus
           *  Load all levels from previously expanded menus
           *  Only do this if loadAllLevels is false
           */
          if (response.menuSpecs
              && (this.lmApplicationMenuService.getExpandedMenus()
                && this.lmApplicationMenuService.getExpandedMenus().length > 0)
          ) {
            // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
            await Promise.all(response.menuSpecs.map(async function callback(menuItemSpec: LMMenuItemSpec) {
              if (self.lmApplicationMenuService.getExpandedMenus().includes(menuItemSpec.menuIndex)) {
                menuItemSpec.menu = await self.getSubMenu(menuItemSpec.link);
                menuItemSpec.expanded = true;

                if (menuItemSpec.menuIndex === self.lmApplicationMenuService.getLastMenuItemClicked()) {
                  menuItemSpec.selected = true;
                }

                if (menuItemSpec.menu && menuItemSpec.menu.length > 0) {
                  await Promise.all(menuItemSpec.menu.map(callback));
                }
              }
            }));
          }
        }

        this.selectedMenuSpec = response.menuSpecs;
        this.buildMenuSpecs(this.menuSpecs, response.webApp, response.webAppTitle, this.selectedMenuSpec);
        this.lmApplicationMenuService.updateApplicationMenuSpec(response.menuSpecs, this.webAppMenuNavigationUrl);
        this.webAppMenuSpecs = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName !== this.webAppName && webAppSpec.webAppTitle);
        // if (this.webAppMenuSpecs.length === 0 || this.webAppMenuSpecs.length > this.noCasualApps - 1) {
        //   this.roleSwitcher = true;
        // }

        // Role switcher true for multiple webapps in v2
        this.roleSwitcher = true;

        this.markForRefresh();
        this.updateRequired = true;

        this.lmContextUtilService.setDrillbackDialogElement(this.drillbackPlaceholder, this.lmActionFormDialogService);
        const dialogId = 'ApplicationMenuResponse';

        if (response.buildBaseMenuMessage) {
          this.sohoMessageService
            .message()
            .message(Soho.Locale.translate(response.buildBaseMenuMessage))
            .title('')
            .buttons([
              {
                id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CLOSE ]),
                text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
                click: (e, modal) => {
                  modal.close(true);
                }
              },
            ])
            .open();
        }
      },
      error => {
        this._handleError(error);
      }));
  }

  private async getSubMenu(url: string): Promise<Array<LMMenuItemSpec>> {
    return new Promise((resolve, reject) => {
      this.lmSpecService.getApplicationMenuSpec(url).subscribe((response) => {
        resolve(response.menuSpecs);
      });
    });
  }

  /**
   * Data and reporting options are disabled by default.
   * And should display if included in the configured features list.
   * @param buttonName
   * @private
   */
  public showDataAndReportingOptions(buttonName: LMConfiguredFeatures): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return this.lmContextService.getContext().configuredFeatures.includes(buttonName);
  }

  /**
   * Show if not in configured features list.
   * Application menu options are enabled by default.
   * If application menu option is in config features list then it was disabled and shouldn't be displayed
   * @param buttonName
   * @private
   */
  public evaluateApplicationMenuOption(buttonName: LMConfiguredFeatures): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return !this.lmContextService.getContext().configuredFeatures.includes(buttonName);
  }

  /**
   * Determines if proxy should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display proxy
   * @private
   */
  private evaluateProxy(applicationMenuSpec: LMApplicationMenuSpec): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return applicationMenuSpec.proxyEnabled || applicationMenuSpec.proxyMode || applicationMenuSpec.proxyManagementLink !== undefined;
  }

  /**
   * Determines if "as of date" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "as of date"
   * @private
   */
  private evaluateAsOfDate(applicationMenuSpec: LMApplicationMenuSpec) {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return false;
      }
    }

    return applicationMenuSpec.allowSetAsOfDate;
  }

  /**
   * Determines if "Create Report" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "Create Report"
   * @private
   */
  private evaluateCreateReportUrl(applicationMenuSpec: LMApplicationMenuSpec): string | undefined {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return undefined;
      }
    }

    return applicationMenuSpec.createReportUrl;
  }

  /**
   * Determines if "personalization" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "personalization"
   * @private
   */
  private evaluatePersonalization(applicationMenuSpec: LMApplicationMenuSpec): boolean {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return undefined;
      }
    }

    return applicationMenuSpec.personalizationEnabled;
  }

  /**
   * Determines if "search" should be displayed in the template
   * If allowAnonymousAccess, config features should be enabled to display "search"
   * @private
   */
  private evaluateShowSearch() {
    if (this.allowAnonymousAccess) {
      if (!this.configFeaturesEnabled) {
        return undefined;
      }
    }

    return !this.lmContextService.getContext().configuredFeatures.includes('HideMenuSearch');
  }

  private updateMenuButtons() {
    const notificationsButton = {
      icon: 'notification',
      label: Soho.Locale.translate('Notifications'),
      id: 'Notifications_button',
      automationId: 'lm-appmenu-notifications-button',
      url: this.notificationsUrl
    };

    const createReportButton = {
      icon: '',
      label: Soho.Locale.translate('CreateReport'),
      id: 'CreateReport_link',
      automationId: 'lm-appmenu-create-report-button',
      url: ''
    };

    this.applicationMenuButtons = new Array();

    if (this.evaluateApplicationMenuOption('HideMenuPrintFiles')) {
      if (this.mingleMode) {
        this.printFilesButton = {
          icon: 'print',
          label: Soho.Locale.translate('PrintFiles'),
          id: 'PrintFiles_button',
          automationId: 'lm-appmenu-print-files-button',
          url: this.printFilesUrl
        };
      } else {
        this.applicationMenuButtons.push({
          icon: 'print',
          label: Soho.Locale.translate('PrintFiles'),
          id: 'PrintFiles_button',
          automationId: 'lm-appmenu-print-files-button',
          url: this.printFilesUrl
        });
      }
    }

    // Notifications button V1 or old banner
    if (this.notificationEnabled && !this.lmContextService.isApplicationMenuV2()) {
      this.applicationMenuButtons.push(notificationsButton);
    }

    // Notifications button if v2
    if ((this.notificationEnabled && this.lmContextService.isApplicationMenuV2()) && this.evaluateApplicationMenuOption('HideMenuNotifications')) {
      this.notificationsButton = notificationsButton;
    }

    // Reports button
    if (this.showDataAndReportingOptions('CreateReport')) {
      this.createReportButton = createReportButton;
    }

    if (this.inbasketEnabled && this.evaluateApplicationMenuOption('HideMenuInbasket')) {
      if (this.mingleMode) {
        this.inbasketButton = {
          icon: 'download',
          label: Soho.Locale.translate('Inbasket'),
          id: 'Inbasket_button',
          automationId: 'lm-appmenu-in-basked-button',
          url: this.inBasketUrl
        };
      } else {
        this.applicationMenuButtons.push({
          icon: 'download',
          label: Soho.Locale.translate('Inbasket'),
          id: 'Inbasket_button',
          automationId: 'lm-appmenu-in-basket-button',
          url: this.inBasketUrl
        });
      }
    }

    if (this.evaluateApplicationMenuOption('HideMenuJobs')) {
      this.applicationMenuButtons.push({
        icon: 'cascade-objects',
        label: Soho.Locale.translate('Jobs'),
        id: 'Jobs_button',
        automationId: 'lm-appmenu-jobs-button',
        url: this.jobsUrl
      });
    }

    if (this.evaluateApplicationMenuOption('HideMenuReports')) {
      this.applicationMenuButtons.push({
        icon: 'report',
        label: Soho.Locale.translate('Reports'),
        id: 'Reports_button',
        automationId: 'lm-appmenu-reports-button',
        url: this.reportsUrl
      });
    }

    if (this.evaluateApplicationMenuOption('HideMenuActions')) {
      this.applicationMenuButtons.push({
        icon: 'checkbox',
        label: Soho.Locale.translate('Actions'),
        id: 'Actions_button',
        automationId: 'lm-appmenu-actions-button',
        url: this.actionsUrl
      });
    }

    if (this.personalizationEnabled) {
      this.applicationMenuButtons.push({
        icon: 'quick-edit',
        label: Soho.Locale.translate('Personalizations'),
        id: 'Personalization_button',
        automationId: 'lm-appmenu-personalization-button',
        url: this.personalizationsUrl
      });
    }

    if (this.enableSavedSearch) {
      this.applicationMenuButtons.push({
        icon: 'print-preview',
        label: Soho.Locale.translate('SavedSearches'),
        id: 'SavedSearches_button',
        automationId: 'lm-appmenu-savedSearches-button',
        url: this.mySavedSearchesUrl
      });
    }
  }

  private useAppSwitcher(): boolean {
    return (this.appSwitcher === 'casual' || this.appSwitcher === 'popup' || this.appSwitcher === 'accordion' || this.appSwitcher === 'suite');
  }

  private markForRefresh() {
    this.changeDetectorRef.markForCheck();
  }

  private _handleError(error: any): void {
    this.lmErrorUtilService.handleUnexpectedError(error);
  }

  private getUnreadNotificationCount() {
    let unreadCountUrl = this.lmUrlUtilService.getBannerSpecUrl();
    unreadCountUrl += (unreadCountUrl.indexOf('?') > -1 ? '&' : '?') + 'requestType=unreadCount';
    this.pause$.next(true);
    this.lmSpecService.getApplicationMenuSpec(unreadCountUrl).subscribe(
      (response: any) => {
        this.pause$.next(false);
        this.unreadNotificationCount = (response.unreadCount) ? parseInt(response.unreadCount, 10) : 0;
        this.displayRedDot = this.unreadNotificationCount > 0;
        this.markForRefresh();
      },
      error => {
        // In 3.5, the error is logged in the console for unread count and not prompted in dialog.
        // So timeout or when server down will not open up a dialog for a background poll request.
        // this._handleError(error);
        this.lmLoggerService.log([ this, error.status, error.message, 'Get unread notification count failed.' ]);
      });
  }

  public applicationMenuSearch() {
    this.showBusyIndicator();
    const menuSearchUrl = this.lmApplicationMenuSearchService.getMenuSearchUrl(this.model.searchFieldValue);
    const observables: Observable<any>[] = [];

    if (this.searchMenuSpecs === undefined) {
      this.searchMenuSpecs = [];
    }

    this.subscriptionArray.push(this.lmSpecService.getRedirectUrl(menuSearchUrl).subscribe(response => {
      const dataMenus = response.dataModules;
      const reports = response.reports;
      const totalWebAppMenu = this.applicationMenuSpecs.length + 1;

      if (totalWebAppMenu > this.searchMenuSpecs.length) {
        this.applicationMenuSpecs.forEach((webApp) => {
          const menu = this.searchMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === webApp.webAppName);
          if (webApp.navigationMenuUrl && menu.length === 0) {
            const url = this.lmUrlUtilService.appendToQueryString(webApp.navigationMenuUrl, 'allLevels=true');
            observables.push(this.lmSpecService.getApplicationMenuSpec(url).pipe(catchError(error => of(this.handleForkJoinError(error)))));
          }
        });
        if (observables.length > 1) {
          this.subscriptionArray.push(forkJoin(observables).subscribe(results => {
            if (results.length > 0) {
              results.forEach((result) => {
                if (result && result.webApp) {
                  this.buildMenuSpecs(this.searchMenuSpecs, result.webApp, result.webAppTitle, result.menuSpecs);
                }
              });
            }
            this.searchMenu(dataMenus, reports);
          }));
        } else {
          this.searchMenu(dataMenus, reports);
        }
      } else {
        this.searchMenu(dataMenus, reports);
      }
    },
    error => {
      this.hideBusyIndicator();
      this._handleError(error);
    }));
  }

  private handleForkJoinError(error) {
    if (error.status === 504) {
      console.error(`Searching ${error.error.webApp} has timed out`);
    }
  }

  private searchMenu(dataMenus: any, reports: any) {
    this.menuSearchResultsByWebApp = this.lmApplicationMenuSearchService.matchMultiMenus(this.menuSpecs, this.model.searchFieldValue);
    this.dataSearchResult = this.lmApplicationMenuSearchService.matchData(dataMenus);
    this.reportSearchResult = this.lmApplicationMenuSearchService.matchReports(reports);

    this.searchMode = true;

    const event: LMApplicationMenuSearchEvent = {
      searchString:              this.model.searchFieldValue,
      menuSpecsByWebApp:         this.menuSpecs,
      menuSearchResultsByWebApp: this.menuSearchResultsByWebApp,
      dataSearchResults:         this.dataSearchResult,
      reportSearchResults:       this.reportSearchResult,
      toggleMenu:                Object.keys(this.menuSearchResultsByWebApp).length > 0 ? 'Applications' : this.reportSearchResult.length > 0 ? 'Reports' : 'Data'
    };
    this.model.searchFieldValue = '';
    this.menuSearched.emit(event);
    this.hideBusyIndicator();
  }

  autoSearchMenu(searchValue: string) {
    this.searchFieldText$.next(searchValue);
  }

  // Save this code for menu search implementation
  fireMenuSearchEvent(event: Event) {
    if (!this.model.searchFieldValue) {
      // if value is blank then fire clearSearchEvent event
      this.fireClearSearchEvent(event);
    } else {
      // if searchfieldvalue length is 3 or more the auto search with perform this.applicationMenuSearch();
      if (this.model.searchFieldValue.length <= 2) {
        this.applicationMenuSearch();
      }
    }
  }

  fireClearSearchEvent(event) {
    this.searchMode = false;

    const toolbarSearchEvent: LMToolbarSearchEvent = {
      event,
      searchField: this.menuSearchField
    };
  }

  public openProxyPage() {
    const dialogId = 'Proxy';
    let title: string;

    if (this.proxyEnabled) {
      title = Soho.Locale.translate('SwitchToProxy');
    } else if (this.proxyMode) {
      title = Soho.Locale.translate('SwitchBackFromProxy');
    } else {
      title = Soho.Locale.translate('ProxyManagement');
    }

    let proxyMenuComponent: LMApplicationMenuProxyMenuComponent;
    this.dialog = this.sohoModalDialogService
      .modal(LMApplicationMenuProxyMenuComponent, this.placeholder, { fullsize: 'responsive' })
      .apply((proxyMenu: LMApplicationMenuProxyMenuComponent) => {
        proxyMenuComponent = proxyMenu;
        if (this.proxyMode) {
          proxyMenuComponent.proxyMode = this.proxyMode;
          proxyMenuComponent.proxyResetUrls = this.proxyResetUrls;
        }
        if (this.proxyEnabled) {
          proxyMenuComponent.proxyEnabled = this.proxyEnabled;
          proxyMenuComponent.proxyUserUrls = this.proxyUserUrls;
        }
        if (this.proxyManagementLink) {
          proxyMenuComponent.proxyManagementLink = this.proxyManagementLink;
        }
      })
      .buttons([
        {
          id: this.lmUniqueIdService.uniqueId([ dialogId, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            modal.isCancelled = true;
            modal.close(true);
          }
        }
      ])
      .title(title)
      .open();
  }

  // todo: vwiny spec-url-refactor doesn't seem to be used. - Phillip
  public openChangePasswordWindow() {
    const subscription = this.lmSpecService.getFormSpecFromRedirectActionUrl(this.changePasswordLink)
      .subscribe((response: LMActionFormUrlResponse | LMSystemException) => {

        if ((response as LMSystemException).status
          && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
            || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
          // TODO: This function is currently not in use. The caller of this function will determine what to do with busy indicator or how to display the error message.
          // this.lmBusyIndicatorService.hideBusyIndicator();
          // this.lmErrorUtilService.handleError((response as LMSystemException).statusText, (response as LMSystemException).details);
          return;
        }

        const formUrlInfo = (response as LMActionFormUrlResponse).formUrlInfo;
        const formSpec = (response as LMActionFormUrlResponse).formSpec;
        const resourceId = (response as LMActionFormUrlResponse).resourceId;
        const actionName = (response as LMActionFormUrlResponse).action;
        const actionSpec: LMActionSpec = this.lmApplicationMenuService.getChangePasswordAction(formSpec.busClassSpec.actions, 'ChangePasswordUIMenu');
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const dataUrl = urlCompiledTemplate.expand({ resourceId, actionName });

        this.lmActionFormDialogService.getActionFormDialogDataView(dataUrl).subscribe((responseDV: any) => {
          const formDataView: LMDataView = responseDV;

          if (formDataView.status !== AppConstants.STATUS_COMPLETED) {
            this.lmLoggerService.error(responseDV);
            this.lmErrorUtilService.handleError(responseDV.message, responseDV.details, responseDV.status, responseDV.statusText);
            return;
          }

          this.lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView)
            .subscribe((actionFormDialogResult: LMActionFormDialogResult) => {
              if (actionFormDialogResult.result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
                // nothing to do as successful and error message are handled where action is executed
              }
            });
        });
      });

    this.subscriptionArray.push(subscription);
    return false; // todo why return false?
  }

  public openLoginWindow() {
    const loginDialog = this.lmLoginService.promptLoginDialog(this.loginPlaceholder).subscribe((result: LoginResult) => {
      if (result.status === LoginDialogStatus.RESPONSE_OK) {
        this.lmLoggerService.log(result);
      }
    });

    this.subscriptionArray.push(loginDialog);
    return false; // todo why return false?
  }

  public webAppSwitcherButtonId: string;

  private onSwitchToProxyClicked(switchProxyUrl: string) {
    this.lmGlobalUtilService.navigate(switchProxyUrl, null, 'replace');
  }

  public runCreateReport(): void {
    this.lmReportPersonalizationDialogService.runReportPersonalizationDialog(
      'report',
      this.reportPlaceholder,
      this.createReportUrl,
      this.actorGroupSelectUrl,
      this.roleSelectUrl,
      this.roleGroupSelectUrl,
      this.reportFieldSearchUrl
    ).subscribe((result: LMReportPersonalizationDialogResult) => {
      if (result.status === LMReportPersonalizationDialogResultCode.REPORT_PERSONALIZATION_DIALOG_SAVED) {
        if (result.viewReport) {
          this.lmGlobalUtilService.navigateByUrl(result.reportUrl);
        } else {
          this.lmGlobalUtilService.displayToastMessage('', result.message, AppConstants.TRA_ACTION_COMPLETED);
        }
      }
      return;
    });
  }

  showBusyIndicator() {
    this.busyIndicator.open();
  }

  hideBusyIndicator() {
    this.busyIndicator.close(false);
  }

  public getApplicationMenuPersonalization() {
    const applicationMenuPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
    if (!_.isEmpty(applicationMenuPersonalization)) {

      if (applicationMenuPersonalization.lastOpenedMenu) {
        this.lmApplicationMenuService.lastOpenedMenuId = applicationMenuPersonalization.lastOpenedMenu;
      } else if (this.lmContextService.menuSearchOpenIndex() != null) {
        this.lmApplicationMenuService.saveLastMenuItemClicked(this.lmContextService.menuSearchOpenIndex());
        this.lmApplicationMenuService.addExpandedMenus(this.lmContextService.menuSearchOpenIndex());
      } else {
        this.lmApplicationMenuService.resetLastOpenedMenu();
      }
    }
  }

  public resetMenuPersonalization() {
    const applicationMenuPersonalization: LMGlobalPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();

    if (!_.isEmpty(applicationMenuPersonalization) && applicationMenuPersonalization.lastOpenedMenu) {
      applicationMenuPersonalization.lastOpenedMenu = '';
    }
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(applicationMenuPersonalization);

    this.lmApplicationMenuService.resetLastOpenedMenu();
  }

  public loadWebAppMenu(button: any): void {
    this.showBusyIndicator();
    const loadAllLevels = this.lmApplicationMenuService.loadAllLevels();

    let url = button.getAttribute('navigationmenuurl');

    const webAppTitle = button.getAttribute('title');
    this.selectedWebAppName = button.getAttribute('id');

    // For webapp without a navigation menu
    if (!url) {
      this.selectedMenuSpec = [];
      const currWebAppSpec = this.applicationMenuSpecs.filter(webAppSpec => webAppSpec.webAppName === this.selectedWebAppName);

      this.selectedMenuSpec.push(
        {
          resourceId:   currWebAppSpec.length === 0 && this.selectedWebAppName === 'web' ? this.selectedWebAppName : currWebAppSpec[ 0 ].webAppName,
          name:         'Home',
          link:         currWebAppSpec.length === 0  && this.selectedWebAppName === 'web' ? url : currWebAppSpec[ 0 ].webAppUrl,
          id:           'Home',
          label:        Soho.Locale.translate('Home'),
          resourceType: 'page'
        });
      this.buildMenuSpecs(this.menuSpecs, this.selectedWebAppName, webAppTitle, this.selectedMenuSpec);

      this.markForRefresh();
      this.updateRequired = true;

      this.hideBusyIndicator();
    } else {
      url = loadAllLevels
        ? `${button.getAttribute('navigationmenuurl')}&allLevels=true`
        : button.getAttribute('navigationmenuurl');

      this.subscriptionArray.push(this.lmSpecService.getApplicationMenuSpec(url).subscribe(
        response => {
          if (loadAllLevels) {
            this.saveAllLevelsResponse(response);
          }

          this.selectedMenuSpec = response.menuSpecs;

          this.buildMenuSpecs(this.menuSpecs, response.webApp, response.webAppTitle, response.menuSpecs);

          this.lmApplicationMenuService.setApplicationMenuSpec(this.selectedMenuSpec);

          this.lmApplicationMenuService.updateApplicationMenuSpec(response.menuSpecs, url);

          this.markForRefresh();
          this.updateRequired = true;

          this.hideBusyIndicator();
        },
        error => {
          this._handleError(error);
        }));
    }
  }

  public selectMenuSpec(webAppName: string) {
    const webAppMenu = this.menuSpecs.filter(webAppSpec => webAppSpec.webAppName === webAppName);
    if (webAppMenu.length === 1) {
      return webAppMenu[ 0 ].menuSpec;
    }
    return [];
  }

  private buildMenuSpecs(
    specs: Array<LMWebAppSpec>,
    webAppName: string,
    webAppTitle: string,
    selectedMenuSpec: Array<any>
  ) {
    let pushMenu = true;

    if (specs.length > 0) {
      const menuSpec = specs.filter(webAppSpec => webAppSpec.webAppName === webAppName);
      if (menuSpec.length > 0) {
        pushMenu = false;
      }
    }
    if (pushMenu) {
      this.menuSpecs.push({
        id:          webAppName,
        webAppName,
        webAppTitle,
        menuSpec:    selectedMenuSpec
      });
    }
  }

  public webAppId(webAppName: string): string {
    return webAppName + (this.lmContextService.testVersion() >= AppConstants.TESTVERSION_V2 ? '_webapp' : '');
  }

  private getActorInitials(): string {
    const actorName = this.actorName;
    const parts = actorName.split(' ');
    const initials = [];

    parts.forEach((p) => {
      p.split('').forEach((s, index) => {
        if (index === 0) {
          initials.push(s);
        }
      });
    });

    return initials.join('');
  }
}
